import {
  Box,
  Button as MuiButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { PageTitle } from "@/components/Title";
import Grid from "@mui/material/Grid";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { Label } from "@/components/Label";

import { KeyboardArrowLeft } from "@mui/icons-material";
import React, { useState } from "react";
import { useFeatureFlags } from "@/configs/featureFlag";
import { FileUploadButton } from "@/features/realEstateReceptionBookFeed/components/FileUploadButton";
import { Button, ButtonVariantOption } from "@/components/Button";
import type {
  AcquireMultipleBookHandoffData,
  BookTypeEng,
} from "@/types/acquirebook";
import { useApiClient } from "@/hooks/useApiClient";
import { isAxiosError } from "axios";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { postPictureRequestFileApi } from "@/features/acquireMultipleBooks/api";

const MIME_CSV = "text/csv";
const MIME_XLSX =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const buildErrorMessage = (type: string): string => {
  if (type === MIME_CSV) {
    return "CSVファイルのアップロードに失敗しました。ファイル内容をご確認ください。";
  } else if (type === MIME_XLSX) {
    return "エクセルファイルのアップロードに失敗しました。ファイル内容をご確認ください。";
  } else {
    return "ファイルのアップロードに失敗しました。ファイル内容をご確認ください。";
  }
};

export const AcquireMultipleBooksUpload: React.FC = () => {
  const { mypageTop } = useFeatureFlags();
  const { apiClient } = useApiClient();
  const navigate = useNavigate();

  // ファイルアップロードダイアログの確認ボタンの有効/無効を決定するフラグ
  const [fileUploadConfirmButtonDisabled, setFileUploadConfirmButtonDisabled] =
    useState<boolean>(true);

  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  const [errorMessage, setErrorMessage] = useState<string>("");

  // /api/picture-request/fileを呼び出した結果レスポンスのフォーマットを変換する
  // これはあとで変更する可能性大
  // acquireMultipleBooks/components/FeedGrid.tsxで処理すべきかも。要検討
  const convertFileParseResult = (
    data: Array<Record<string, string>>
  ): AcquireMultipleBookHandoffData[] => {
    if (data.length > 0) {
      return data.map((value: Record<string, string>, index: number) => {
        return {
          id: index + 1,
          bookId: Number(value.id),
          bookType: value.realEstateType as BookTypeEng,
          prefCode: value.prefCode,
          prefName: value.prefName,
          locationFull: value.locationFull,
          chibanKaokuNumber: value.chibanKaokuNumber,
          kyoutan: false,
          sintaku: false,
          genzai: false,
          ownerInfo: false,
          electricDrawings: false,
          tisekiDrawings: false,
          tiekiDrawings: false,
          buildingDrawings: false,
        };
      });
    }
    return [];
  };

  // ファイルアップロードの確認ボタンが押された時の処理
  const handleFileUploadButtonClick = (): void => {
    if (fileToUpload) {
      postPictureRequestFileApi(apiClient, fileToUpload)
        .then((data) => {
          // 受信したレスポンスを次のページへの形式へ変換する
          const row = convertFileParseResult(data);

          // 確認ページへ遷移する
          navigate("/acquiremultiplebooks", { state: row });
        })
        .catch((e) => {
          console.error(e);
          const nonFieldErrors = isAxiosError<{ nonFieldErrors?: string[] }>(e)
            ? e.response?.data?.nonFieldErrors
            : undefined;
          if (nonFieldErrors && nonFieldErrors.length > 0) {
            setErrorMessage(nonFieldErrors.join("\n"));
          } else {
            setErrorMessage(buildErrorMessage(fileToUpload.type));
          }
        });
    }
  };

  return (
    <PagePaper>
      <form>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* ページタイトル */}
          <Box sx={{ display: "flex", mb: 3 }}>
            <RequestQuoteIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>
              地番・家屋番号リストから不動産登記簿を取得する
            </PageTitle>
          </Box>
          <Box>
            <MuiButton
              href="https://trustart.notion.site/4-1d114754f2df806f9991e47fdee087bd#1d114754f2df814eb498c4db6c98450d"
              target="_blank"
              startIcon={<AutoStoriesIcon />}
              variant="text"
            >
              マニュアル
            </MuiButton>
          </Box>
        </Box>
        <Stack spacing={1} sx={{ ml: 5, mb: 3, width: "fit-content" }}>
          <Link href={"/assets/sample_登記図面請求アップロードファイル.csv"}>
            見本CSVをダウンロード
          </Link>
          <Link href={"/assets/sample_登記図面請求アップロードファイル.xlsx"}>
            見本Excelをダウンロード
          </Link>
        </Stack>
        <SectionPaper>
          <Box sx={{ m: 3 }}>
            <Grid container justifyContent="center">
              <Grid>
                <Grid container direction="column" spacing={4}>
                  <Grid item>
                    <Label required={true}>ファイルアップロード</Label>
                    <Box display={"flex"} justifyContent="start" my={2} gap={3}>
                      <FileUploadButton
                        variant={ButtonVariantOption.Outlined}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          // ファイルが選択されているときのみ確認画面へボタンを有効にする
                          const files = event.target.files ?? [];
                          if (files.length > 0) {
                            setFileToUpload(files[0]);
                            setFileUploadConfirmButtonDisabled(false);
                            setErrorMessage("");
                          } else {
                            if (fileToUpload === null) {
                              setFileUploadConfirmButtonDisabled(true);
                            }
                          }
                        }}
                        accept={".csv, .xlsx, .xls"}
                        label={"アップロード"}
                      />
                      <Typography variant="body1" sx={{ lineHeight: 2 }}>
                        {fileToUpload
                          ? fileToUpload?.name
                          : "選択されていません"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ lineHeight: 2 }}
                      color={"text.secondary"}
                    >
                      アップロードボタンを押してファイルを選択してください
                    </Typography>
                  </Grid>
                  {errorMessage && (
                    <Grid item>
                      <Box>
                        <Typography
                          color="error"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {errorMessage}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mx: "auto", my: 2 }}>
            <Button
              variant={ButtonVariantOption.Contained}
              onClick={handleFileUploadButtonClick}
              disabled={fileUploadConfirmButtonDisabled}
              label={"確認画面へ"}
            />
          </Box>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>
    </PagePaper>
  );
};
